import React, { useState, useEffect } from 'react';

import {
  MailOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Tag,
  Popconfirm,
  Tooltip
} from 'antd';
import { useTranslation } from 'react-i18next';

import TemplateSelect from '../permissions/TemplateSelect';

/**
 * User Invitations 列表組件
 *
 * @param {Object} userInvitationService - User Invitation Service
 * @param {Object} [templateService] - Required when role === 'admin'; ignored otherwise
 * @param {Number} merchantId - Merchant ID（僅 operator 需要）
 * @param {String} role - 角色 ('admin' 或 'operator')
 * @param {Boolean} hideCard - 是否隱藏外層 Card（由父組件控制佈局，舊 API）
 * @param {Boolean} showCard - 是否顯示外層 Card（新 API，預設 true；設定後會覆蓋 hideCard）
 * @param {Number} refreshKey - 由父組件控制的 refetch 觸發器，值改變時重新拉取
 * @param {Function} onModalVisibleChange - Modal 狀態變更時的回調
 * @param {Boolean} modalVisible - 外部控制 Modal 顯示狀態
 */
const UserInvitationsList = ({
  userInvitationService,
  templateService,
  merchantId,
  role = 'operator',
  hideCard = false,
  showCard,
  refreshKey = 0,
  onModalVisibleChange,
  modalVisible: externalModalVisible
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [internalModalVisible, setInternalModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // 支援外部或內部控制 Modal
  const isModalVisible = externalModalVisible !== undefined ? externalModalVisible : internalModalVisible;
  const setIsModalVisible = (visible) => {
    if (onModalVisibleChange) {
      onModalVisibleChange(visible);
    } else {
      setInternalModalVisible(visible);
    }
  };

  useEffect(() => {
    if (role === 'admin' || merchantId) {
      fetchInvitations();
    }
  }, [merchantId, role, refreshKey]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const data = await userInvitationService.fetchList(merchantId);

      if (data.success && data.data) {
        setInvitations(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      message.error(t(`common.merchants.invitations.error.fetch_failed`));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      const { permission_template_id, ...rest } = values;
      const payload = role === 'admin'
        ? { ...rest, permissions: selectedPermissions }
        : { ...rest };
      const data = await userInvitationService.create(merchantId, payload);

      if (data.success) {
        message.success(t(`common.merchants.invitations.messages.create_success`));
        setIsModalVisible(false);
        form.resetFields();
        setSelectedPermissions([]);
        fetchInvitations();
      } else {
        // 從 errors 陣列取得錯誤碼，並翻譯
        const errorCode = data.errors?.[0]?.detail;
        const errorMessage = errorCode
          ? t(`common.merchants.invitations.error.${errorCode}`, { defaultValue: t(`common.merchants.invitations.error.create_failed`) })
          : t(`common.merchants.invitations.error.create_failed`);
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Failed to create invitation:', error);
      message.error(t(`common.merchants.invitations.error.create_failed`));
    }
  };

  const handleCopyLink = (url) => {
    console.log('[UserInvitationsList] Copy link URL:', url);
    if (!url) {
      message.error(t(`common.merchants.invitations.error.no_link`));
      return;
    }
    navigator.clipboard.writeText(url);
    message.success(t(`common.merchants.invitations.messages.link_copied`));
  };

  const handleRegenerate = async (id) => {
    try {
      const data = await userInvitationService.regenerate(merchantId, id);

      if (data.success) {
        message.success(t(`common.merchants.invitations.messages.regenerate_success`));
        fetchInvitations();
      } else {
        message.error(data.message || t(`common.merchants.invitations.error.regenerate_failed`));
      }
    } catch (error) {
      console.error('Failed to regenerate invitation:', error);
      message.error(t(`common.merchants.invitations.error.regenerate_failed`));
    }
  };

  const handleDelete = async (id) => {
    try {
      const data = await userInvitationService.delete(merchantId, id);

      if (data.success) {
        message.success(t(`common.merchants.invitations.messages.delete_success`));
        fetchInvitations();
      } else {
        message.error(data.message || t(`common.merchants.invitations.error.delete_failed`));
      }
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      message.error(t(`common.merchants.invitations.error.delete_failed`));
    }
  };

  const getStatusTag = (record) => {
    if (record.status === 'accepted') {
      return <Tag icon={<CheckCircleOutlined />} color="success">{t(`common.merchants.invitations.status.accepted`)}</Tag>;
    }
    if (record.expired) {
      return <Tag icon={<CloseCircleOutlined />} color="error">{t(`common.merchants.invitations.status.expired`)}</Tag>;
    }
    if (record.status === 'canceled') {
      return <Tag icon={<CloseCircleOutlined />} color="default">{t(`common.merchants.invitations.status.canceled`)}</Tag>;
    }
    return <Tag icon={<ClockCircleOutlined />} color="processing">{t(`common.merchants.invitations.status.pending`)}</Tag>;
  };

  const columns = [
    {
      title: t(`common.merchants.invitations.fields.email`),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t(`common.merchants.invitations.fields.status`),
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: t('common.audit.inviter', { defaultValue: '邀請人' }),
      dataIndex: 'inviter',
      key: 'inviter',
      render: (inviter) => inviter?.name || '-',
      width: '120px',
    },
    {
      title: t(`common.merchants.invitations.fields.expires_at`),
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: t(`common.created_at`),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && !record.expired && (
            <>
              <Tooltip title={t(`common.merchants.invitations.actions.copy_link`)}>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyLink(record.invitation_url)}
                />
              </Tooltip>
              <Tooltip title={t(`common.merchants.invitations.actions.regenerate`)}>
                <Popconfirm
                  title={t(`common.merchants.invitations.confirm.regenerate`)}
                  onConfirm={() => handleRegenerate(record.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<ReloadOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
              <Tooltip title={t(`common.merchants.invitations.actions.cancel`)}>
                <Popconfirm
                  title={t(`common.merchants.invitations.confirm.delete`)}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            </>
          )}
          {record.status === 'accepted' && record.user && (
            <span style={{ color: '#999', fontSize: '12px' }}>
              {record.user.name}
            </span>
          )}
        </Space>
      ),
    },
  ];

  const tableContent = (
    <Table
      dataSource={invitations}
      columns={columns}
      rowKey="id"
      loading={loading}
      pagination={{ pageSize: 10 }}
    />
  );

  // `showCard` 為新 API；若未傳則 fallback 到舊 `hideCard` 邏輯
  const shouldShowCard = showCard !== undefined ? showCard : !hideCard;

  return (
    <>
      {!shouldShowCard ? tableContent : (
        <Card
          title={
            <Space>
              <MailOutlined />
              {t(`common.merchants.invitations.title`)}
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              {t(`common.merchants.invitations.actions.create`)}
            </Button>
          }
        >
          {tableContent}
        </Card>
      )}

      <Modal
        title={t(`common.merchants.invitations.modal.create_title`)}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setSelectedPermissions([]);
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="email"
            label={t(`common.merchants.invitations.fields.email`)}
            rules={[
              { required: true, message: t(`common.merchants.invitations.validation.email_required`) },
              { type: 'email', message: t(`common.merchants.invitations.validation.email_invalid`) }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          {role === 'admin' && (
            <Form.Item
              name="permission_template_id"
              label={t('common.merchants.invitations.fields.permission_template')}
            >
              <TemplateSelect
                templateService={templateService}
                role="admin"
                onChange={(templateId, permissions) => {
                  form.setFieldsValue({ permission_template_id: templateId });
                  setSelectedPermissions(permissions);
                }}
              />
            </Form.Item>
          )}
          {role === 'operator' && (
            <div style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)' }}>
              {t('common.merchants.invitations.operator_super_admin_notice', {
                defaultValue: '此邀請將以 super admin 身份建立。被邀請者接受後，可在 admin 後台自行管理權限。'
              })}
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('common.button.create')}
              </Button>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
                setSelectedPermissions([]);
              }}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserInvitationsList;
